// BDEPerformanceChart.tsx
import React, { useEffect, useState } from "react";
import { Card, Row, Col, Typography, Spin } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { performanceData as defaultPerformanceData, chartSeries as defaultChartSeries } from "./bdeConfig";
import { getData } from "../../../../../../actions/apiCall";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface BDEPerformanceChartProps {
  bdeId?: string | null;
  startDate?: dayjs.Dayjs | null;
  endDate?: dayjs.Dayjs | null;
  period?: string;
}

const BDEPerformanceChart: React.FC<BDEPerformanceChartProps> = ({
  bdeId,
  startDate,
  endDate,
  period = 'month'
}) => {
  const [performanceData, setPerformanceData] = useState(defaultPerformanceData);
  const [chartSeries, setChartSeries] = useState(defaultChartSeries);
  const [targetPercentage, setTargetPercentage] = useState(72);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { user, isGlobalAdmin } = useSelector((state: any) => state.app);
  const isAdmin = isGlobalAdmin || user?.designation === 'Admin';

  useEffect(() => {
    // Only fetch if we have required filters
    if (startDate && endDate) {
      // For admin: fetch even without bdeId (overall data)
      // For non-admin: require bdeId
      if (isAdmin || bdeId) {
        fetchPerformanceData();
      }
    }
  }, [bdeId, startDate, endDate, period, isAdmin]);

  const fetchPerformanceData = async () => {
    if (!startDate || !endDate) return;
    // For non-admin, require employee selection
    if (!isAdmin && !bdeId) return;

    try {
      setLoading(true);
      const startDateStr = startDate.format('YYYY-MM-DD');
      const endDateStr = endDate.format('YYYY-MM-DD');
      const bdeIdParam = bdeId === 'all' || !bdeId ? 'all' : bdeId;

      const res = await dispatch(
        getData(`/targets/bde/performance-overview?bdeId=${bdeIdParam}&startDate=${startDateStr}&endDate=${endDateStr}&period=${period}`)
      );

      if (res?.code === 200 && res?.data) {
        setPerformanceData(res.data.performanceData || defaultPerformanceData);
        setChartSeries(res.data.chartSeries || defaultChartSeries);
        setTargetPercentage(res.data.targetPercentage || 0);
      }
    } catch (error) {
      console.error('Error fetching performance overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      bordered
      className="w-full rounded-xl shadow-sm border-gray-200 mt-6"
      bodyStyle={{ padding: "20px 24px" }}
    >
      {/* Header */}
      <Row>
        <Col span={24}>
          <Title level={5} className="!m-0 !text-gray-800">
            BDE Performance Overview
          </Title>

          <Text className="text-gray-500 text-sm">
            Target: <span className="font-semibold">{targetPercentage}%</span>; Achieved
          </Text>
        </Col>
      </Row>

      {/* Chart */}
      <div className="w-full h-[320px] mt-4">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <Spin size="large" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={performanceData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip />
              <Legend />

              {chartSeries.map((series) => (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  stroke={series.color}
                  strokeWidth={3}
                  dot={{ r: 5 }}
                  activeDot={{ r: 6 }}
                  name={series.label}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default BDEPerformanceChart;
